// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { useState, useRef } from "react";
import { Controller } from "react-hook-form";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export const MaskedPasswordControl = ({
    name,
    id,
    control,
    label,
    maxLength,
    sx,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [selectAllMode, setSelectAllMode] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const inputRef = useRef(null);

    return (
        <Controller
            name={name}
            control={control}
            defaultValue=""
            render={({ field }) => {
                const realValue = field.value || "";
                const maskedValue = "•".repeat(realValue.length);
                const displayValue = showPassword ? realValue : maskedValue;

                const handlePaste = (e) => {
                    e.preventDefault();

                    const pastedText = e.clipboardData.getData("text");
                    if (!pastedText) return;

                    const clean = pastedText.replace(/\s/g, "");

                    const nextValue = selectAllMode
                        ? clean
                        : realValue + clean;

                    field.onChange(
                        maxLength ? nextValue.slice(0, maxLength) : nextValue
                    );

                    setSelectAllMode(false);
                };

                const handleKeyDown = (e) => {
                    const key = e.key;

                    // CTRL+A → activate virtual select-all
                    if (e.ctrlKey && key.toLowerCase() === "a") {
                        setSelectAllMode(true);
                        e.preventDefault();
                        return;
                    }

                    // If select-all mode active and user presses Backspace → clear everything
                    if (selectAllMode && key === "Backspace") {
                        field.onChange("");
                        setSelectAllMode(false);
                        e.preventDefault();
                        return;
                    }

                    // Any normal keypress ends select-all mode
                    if (selectAllMode && key.length === 1) {
                        setSelectAllMode(false);
                    }
                };

                const handleChange = (e) => {
                    const nextValue = e.target.value;

                    if (showPassword) {
                        field.onChange(
                            maxLength ? nextValue.slice(0, maxLength) : nextValue
                        );
                        return;
                    }

                    let newRealValue = "";
                    let realIndex = 0;

                    for (let i = 0; i < nextValue.length; i++) {
                        const char = nextValue[i];
                        if (char === "•") {
                            if (realIndex < realValue.length) {
                                newRealValue += realValue[realIndex];
                                realIndex++;
                            } else {
                                newRealValue += char; // Handle case where bullet is treated as char
                            }
                        } else {
                            newRealValue += char;
                        }
                    }

                    field.onChange(
                        maxLength ? newRealValue.slice(0, maxLength) : newRealValue
                    );
                };

                const toggleShow = () => {
                    setShowPassword((prev) => !prev);
                    setTimeout(() => {
                        if (inputRef.current) inputRef.current.focus();
                    }, 0);
                };

                return (
                    <TextField
                        {...field}
                        id={id}
                        label={label}
                        type="text" // never type=password so no autofill
                        value={displayValue}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onChange={handleChange}
                        onFocus={() => setIsReadOnly(false)}
                        autoComplete="off"
                        inputRef={inputRef}
                        sx={sx}
                        InputProps={{
                            autoComplete: "off",
                            readOnly: isReadOnly,
                            form: {
                                autoComplete: "off"
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={toggleShow} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            autoComplete: "off",
                            "data-form-type": "other",
                            form: {
                                autoComplete: "off"
                            }
                        }}
                    />
                );
            }}
        />
    );
};
